import { SkyAccordion, SkyAccordionItem, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { FieldRenderer } from "./FieldRenderer";
import type {
  FieldDefinition,
  FieldErrors,
  FieldGroupDefinition,
  FieldStateMap,
  FormValues,
} from "./types";
import { pinnedFieldHint } from "./pinnedFields";

/**
 * The connector's field groups, in declared order (FR-40, UX-4).
 *
 * A rewrite rather than a port: skypoint-studio's `FieldGroup.tsx` was entirely
 * Fluent `Accordion` primitives, and `SkyAccordion` already does the disclosure,
 * the heading button and the keyboard handling, so the component that survives
 * is thin.
 *
 * Every group renders as an accordion item so the column reads as one list
 * whichever groups a connector declares. A group the seed marks
 * `collapsible: false` starts OPEN, and that is all the flag means here: its
 * heading is a working disclosure like every other. Holding it open made the
 * chevron and the button look broken (a click did nothing), and skypoint-ui has
 * no "not a disclosure" item to render instead.
 *
 * Because any group can now be closed, a required field can sit inside a
 * collapsed one; when a save shows errors, every group that gains one re-opens.
 */

export interface FieldGroupsProps {
  groups: FieldGroupDefinition[];
  fields: FieldDefinition[];
  values: FormValues;
  fieldState: FieldStateMap;
  errors: FieldErrors;
  /** Errors stay hidden until the user has tried to save or validate... */
  showErrors: boolean;
  /**
   * ...or has left the field (#1198): a field in this set shows its error at once, so a required
   * field left empty says so before any Save.
   */
  touched?: ReadonlySet<string>;
  /** Focus left a field (its control, or any part of a composite one). */
  onFieldBlur?: (fieldId: string) => void;
  /** Read-only (`dataflows.read` without `dataflows.write`) disables every control. */
  readOnly?: boolean;
  onChange: (fieldId: string, value: unknown) => void;
  onBrowse?: (fieldId: string) => void;
  /** Rendered under a group's description, e.g. the re-validate action under credentials. */
  groupFooter?: (groupId: string) => React.ReactNode;
  /**
   * #1107: the fields whose value this reader may not see (a source host, user name or email,
   * below `dataflows.write`). Each reads "Hidden" with the reason, never an empty box.
   */
  withheld?: ReadonlySet<string>;
  /**
   * #1932: fields the environment pins (the Google service account), by id. Each shows its value
   * read-only with the reason under it; the value is in `values` already (`withPinnedValues`).
   */
  pinned?: ReadonlyMap<string, string>;
  /** #1932 review: the pinned fields whose stored value is another one (a change to save). */
  pinnedChanged?: ReadonlySet<string>;
}

/** #1107: the words a withheld field reads, with the reason under it. */
export const WITHHELD_VALUE = "Hidden";
export const WITHHELD_REASON = "Only contributors and admins see connection details.";

function WithheldField({ field }: { field: FieldDefinition }) {
  return (
    <SkyInput
      testId={`field-${field.id}`}
      label={field.name}
      value={WITHHELD_VALUE}
      disabled
      hint={WITHHELD_REASON}
      onChange={() => undefined}
    />
  );
}

/**
 * #1932: a field the environment pins, read-only with the reason and the seed's help under it.
 * Read-only, not disabled (review of #2429), so the address can be selected and copied into
 * Google's admin screen.
 */
export function PinnedField({
  field,
  value,
  changed = false,
}: {
  field: FieldDefinition;
  value: string;
  changed?: boolean;
}) {
  return (
    <SkyInput
      testId={`field-${field.id}`}
      label={field.name}
      value={value}
      readOnly
      hint={pinnedFieldHint(field.helpText, changed)}
      onChange={() => undefined}
    />
  );
}

/** Fields that belong to a group and are not hidden by a dependency or by the seed. */
function visibleFieldsOf(
  fields: FieldDefinition[],
  groupId: string,
  fieldState: FieldStateMap,
): FieldDefinition[] {
  return fields
    .filter((field) => field.groupId === groupId)
    .filter((field) => !field.isPlatformManaged)
    .filter((field) => fieldState[field.id]?.visible ?? field.visible)
    .sort((a, b) => a.order - b.order);
}

const NONE_TOUCHED: ReadonlySet<string> = new Set();
const NONE_WITHHELD: ReadonlySet<string> = new Set();
const NONE_PINNED: ReadonlyMap<string, string> = new Map();
const NONE_CHANGED: ReadonlySet<string> = new Set();

export function FieldGroups({
  groups,
  fields,
  values,
  fieldState,
  errors,
  showErrors,
  touched = NONE_TOUCHED,
  onFieldBlur,
  readOnly = false,
  onChange,
  onBrowse,
  groupFooter,
  withheld = NONE_WITHHELD,
  pinned = NONE_PINNED,
  pinnedChanged = NONE_CHANGED,
}: FieldGroupsProps) {
  const ordered = useMemo(() => [...groups].sort((a, b) => a.order - b.order), [groups]);

  const [openItems, setOpenItems] = useState<string[]>(() => {
    const expanded = ordered
      .filter((group) => group.defaultExpanded || !group.collapsible)
      .map((group) => group.id);
    return Array.from(new Set(expanded));
  });

  // Groups holding a visible error once errors are shown. A group that GAINS an
  // error (a save that failed validation) opens, so no error is hidden inside a
  // collapsed section. Only a newly erroring group opens: fixing a field in one
  // group shrinks the set, and that must not re-open a group the user closed
  // since (review r1, A2). Adjusted during render rather than in an effect, so
  // the open state and the errors paint together.
  const errorGroups = showErrors
    ? ordered
        .filter((group) =>
          visibleFieldsOf(fields, group.id, fieldState).some((field) => errors[field.id]),
        )
        .map((group) => group.id)
    : [];
  const errorKey = errorGroups.join("|");
  const [openedForErrors, setOpenedForErrors] = useState("");
  if (errorKey !== openedForErrors) {
    const before = new Set(openedForErrors ? openedForErrors.split("|") : []);
    setOpenedForErrors(errorKey);
    const gained = errorGroups.filter((id) => !before.has(id));
    if (gained.length > 0) {
      setOpenItems((current) => Array.from(new Set([...current, ...gained])));
    }
  }

  return (
    <SkyAccordion
      multiple
      size="small"
      testId="connector-field-groups"
      openItems={openItems}
      onToggle={(_value, next) => setOpenItems(next)}
    >
      {ordered.map((group) => {
        const groupFields = visibleFieldsOf(fields, group.id, fieldState);
        const footer = groupFooter?.(group.id);
        if (groupFields.length === 0 && !footer) return null;

        return (
          <SkyAccordionItem key={group.id} value={group.id} heading={group.label}>
            <div className="flex flex-col gap-4 pb-2">
              {group.description ? (
                <SkyText as="p" size={200} color="muted" block>
                  {group.description}
                </SkyText>
              ) : null}
              {groupFields.map((field) => {
                if (withheld.has(field.id)) return <WithheldField key={field.id} field={field} />;
                const pinnedValue = pinned.get(field.id);
                if (pinnedValue !== undefined) {
                  return (
                    <PinnedField
                      key={field.id}
                      field={field}
                      value={pinnedValue}
                      changed={pinnedChanged.has(field.id)}
                    />
                  );
                }
                const state = fieldState[field.id];
                const shown = showErrors || touched.has(field.id);
                return (
                  // Focus leaving the field's own controls (not moving between them) marks it.
                  <div
                    key={field.id}
                    className="contents"
                    data-testid={`field-blur-${field.id}`}
                    onBlur={(event) => {
                      const next = event.relatedTarget;
                      if (next instanceof Node && event.currentTarget.contains(next)) return;
                      onFieldBlur?.(field.id);
                    }}
                  >
                    <FieldRenderer
                      field={field}
                      value={values[field.id]}
                      error={shown ? (errors[field.id] ?? null) : null}
                      disabled={readOnly || (state?.disabled ?? field.disabled)}
                      readOnly={readOnly}
                      required={state?.required ?? field.validation.required?.value ?? false}
                      onChange={onChange}
                      onBrowse={onBrowse}
                    />
                  </div>
                );
              })}
              {footer}
            </div>
          </SkyAccordionItem>
        );
      })}
    </SkyAccordion>
  );
}
